<template>
  <div class="medication-effect-chain">
    <div class="medication-subpanel-header">
      <div>
        <help-tooltip v-if="hasWarning" type="warning">
          <span v-for="warning in warnings" :key="warning.message">
            {{ warning.message }}
            <br>
          </span>
        </help-tooltip>
        <help-tooltip v-if="hasError" type="error">
          <span v-for="error in errors" :key="error.message">
            {{ error.message }}
            <br>
          </span>
        </help-tooltip>
      </div>
    </div>
    <div v-if="state.medication" class="medication-overview new-globals">
      <div class="action-bar">
        <md-switch
          v-if="dosageForm"
          :value="!!dosageForm.useDefaultEffectChain"
          class="ui-theme-3"
          :disabled="readonly"
          @change="value => toggleDefaultEffectChain(value)"
        >
          Use default behavior below?
          <md-tooltip md-direction="top">
            Turning this on will use the default behavior for this medication in the simulation seen below.  If other behavior is desired, edit the effect chain, or edit the behavior in case logic.
          </md-tooltip>
        </md-switch>
        <div v-if="dosageForm && state.defaultEffectChain && hasUpdates" class="update-ec-container">
          <help-tooltip type="info">
            Update to the latest effect chain. Taking this action may require you to update your case accordingly
          </help-tooltip>
          <md-button class="md-primary md-raised" @click="toggleDefaultEffectChain(true)">
            Update Effect Chain
          </md-button>
        </div>
      </div>
      <div class="effect-chain-editor-container">
        <effect-chain-editor
          v-if="state.showEffectChainEditor"
          :id="effectChainId"
          :editor-id="`${state.medication.id.replace(/\./g, '_')}-effect-chain-thumbnail`"
          :readonly="true"
          :case-conf="caseConf"
          @baseEffectChainChange="ec => updateDefaultEffectChain(ec)"
        >
          <div class="effect-chain-editor-actions">
            <div>
              <md-button
                class="md-primary md-raised blue-5 bg"
                @click="editEffectChain()"
              >
                <md-icon>{{ readonly ? 'visibility' : 'edit' }}</md-icon>
                {{ readonly ? 'View' : 'Edit' }}
              </md-button>
            </div>
          </div>
        </effect-chain-editor>
      </div>
    </div>

    <md-dialog
      :md-active="state.editingEffectChain"
      :md-close-on-esc="true"
      class="effect-chain-dialog"
      @md-clicked-outside="exitEffectChainEdit"
    >
      <md-dialog-title>Effect Chain</md-dialog-title>
      <md-dialog-content>
        <effect-chain-editor
          v-if="state.medication && state.showEffectChainEditor"
          :id="effectChainId"
          :editor-id="`${state.medication.id.replace(/\./g, '_')}-effect-chain-modal`"
          :effect-chain="effectChain"
          :readonly="readonly"
          :case-conf="caseConf"
          @update="effectChain => updateEffectChain(effectChain)"
        />
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="exitEffectChainEdit">Close</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import EffectChainEditor from '~lib/case-logic-builder/EffectChainEditor';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import get from 'lodash/get';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';

export default {
  name: 'case-medication-effect-chain',
  components: {
    EffectChainEditor,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    medication: {
      type: Object,
      required: true,
    },
    dosageForm: {
      type: Object,
      default: () => null,
    },
    customEffectChain: {
      type: Object,
      default: () => null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        medication: this.medication,
        dosageForm: this.dosageForm,
        customEffectChain: null,
        defaultEffectChain: null,
        showEffectChainEditor: true,
        editingEffectChain: false,
        pendingThumbnailRefresh: false,
      },
    };
  },
  computed: {
    caption() {
      return this.readonly ? '(turn on a medication to modify it)' : '(make a selection to modify it)';
    },
    effectChainId() {
      let id;
      if (this.dosageForm && this.dosageForm.customEffectChainId && this.dosageForm.useDefaultEffectChain) {
        id = this.dosageForm.customEffectChainId;
      } else {
        id = this.state.medication.effectChainId;
      }
      return id;
    },
    effectChain() {
      const ec = this.state.customEffectChain;
      return ec;
    },
    hasUpdates() {
      const { outdatedMedications } = this.caseConf;
      return !!(outdatedMedications && outdatedMedications.find(med => med.id === this.medication.id))
    },
    warnings() {
      const medicationEcWarnings = uniqBy(
        get(
          this.$store.getters['validation/visibleWarningsByFieldAndId'],
          `medication.${this.medication.id}`,
        ),
        'message',
      ).filter(error => error.subField === 'effectChain');
      return medicationEcWarnings;
    },
    errors() {
      const medicationEcErrors = uniqBy(
        get(
          this.$store.getters['validation/errorsByFieldAndId'],
          `medication.${this.medication.id}`,
        ),
        'message',
      ).filter(error => error.subField === 'effectChain');
      return medicationEcErrors;
    },
    hasWarning() {
      return this.warnings && this.warnings.length;
    },
    hasError() {
      return this.errors && this.errors.length;
    },
  },
  watch: {
    medication(medication) {
      if (!medication) {
        return;
      }
      if (!this.state.medication || this.state.medication.id !== medication.id) {
        this.state.medication = medication;
        this.remountEffectChainEditor();
      }
    },
    dosageForm(dosageForm) {
      const changed = (this.state.dosageForm && !dosageForm) ||
        (!this.state.dosageForm && dosageForm) ||
        (this.state.dosageForm.id !== dosageForm.id);
      if (changed) {
        this.state.dosageForm = dosageForm;
        this.loadCustomEffectChain();
      }
    },
    customEffectChain(customEffectChain) {
      this.state.customEffectChain = customEffectChain;
    },
  },
  mounted() {
    this.loadCustomEffectChain();
  },
  methods: {
    loadCustomEffectChain() {
      if (this.state.dosageForm && this.state.dosageForm.useDefaultEffectChain && this.state.dosageForm.customEffectChainId) {
        this.$emit('fetchById', this.state.dosageForm.customEffectChainId);
      } else {
        this.state.customEffectChain = null;
      }
      this.remountEffectChainEditor();
    },
    remountEffectChainEditor() {
      this.state.showEffectChainEditor = false;
      this.$nextTick(() => {
        this.state.showEffectChainEditor = true;
      });
    },
    editEffectChain() {
      this.state.editingEffectChain = true;
    },
    exitEffectChainEdit() {
      this.state.editingEffectChain = false;
      if (this.state.pendingThumbnailRefresh) {
        this.remountEffectChainEditor();
        this.state.pendingThumbnailRefresh = false;
      }
    },
    toggleDefaultEffectChain(toggledOn) {
      if (!this.dosageForm) {
        return;
      }
      if (toggledOn) {
        this.state.dosageForm.useDefaultEffectChain = true;
        if (!this.dosageForm.customEffectChainId) {
          this.state.customEffectChain = this.state.defaultEffectChain;
          this.emitUpdate();
        }
      } else {
        this.state.dosageForm.useDefaultEffectChain = false;
      }
      this.emitDosageFormUpdate({ useDefaultEffectChain: this.state.dosageForm.useDefaultEffectChain});
      if (toggledOn) {
        this.caseConf.outdatedMedications = this.caseConf.outdatedMedications.filter(
          outdatedMed => outdatedMed.id !== this.state.medication.id
        );
      }
      this.$analytics.event('Authoring Tool', 'medications_default_behavior_toggled',
        this.state.medication.name.en, undefined, { toggledOn });
    },
    updateEffectChain(effectChain) {
      if (this.readonly) {
        return;
      }
      this.state.customEffectChain = effectChain;

      this.emitUpdate(
        () => {
          this.state.pendingThumbnailRefresh = true;
          // Refresh the thumbnail immediately after edit
          // if the dialog is already closed
          if (!this.state.editingEffectChain) {
            this.remountEffectChainEditor();
          }
        },
        (error) => {
          console.log(error)
        }
      );
    },
    updateDefaultEffectChain(effectChain) {
      this.state.defaultEffectChain = effectChain;
    },
    emitDosageFormUpdate(update) {
      const updatedDosageForm = {
        ...this.dosageForm,
        ...update
      };
      this.$emit('updateDosageForm', updatedDosageForm);
    },
    emitUpdate(successCallback, errorCallback) {
      if (!this.dosageForm) {
        return;
      }

      const operation = this.dosageForm.customEffectChainId ? 'save' : 'add';
      const payload = {
        ...omit(this.state.customEffectChain, 'updatedAt'),
        id: this.dosageForm.customEffectChainId,
        type: 'CUSTOM_EFFECT_CHAIN'
      }
      this.$emit(operation, payload, newOrUpdatedEC => {
        if (operation === 'add') {
          this.state.dosageForm.customEffectChainId = newOrUpdatedEC.id;
          this.emitDosageFormUpdate({ customEffectChainId: newOrUpdatedEC.id })
        }
        successCallback(newOrUpdatedEC);
      }, errorCallback);

      if (successCallback) {
        successCallback();
      }
    },
  },
}
</script>

<style scoped>
.medication-effect-chain {
  display: table;
  min-width: 500px;
  width: 100%;
}

.medication-subpanel-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.medication-overview {
  height: 100%;
}

.effect-chain-editor-container {
  position: relative;
  height: 100%;
}

.effect-chain-editor-actions {
  position: absolute;
  bottom: 10px;
  left: 10px;
}

.effect-chain-dialog {
  width: 90%;
  height: 90%;
  max-width: 90%;
  max-height: 90%;
  z-index: 1501;
}

.action-bar {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: 0;
}

.action-bar .update-ec-container {
  display: flex;
  align-items: center;
}

.md-switch {
  margin-top: 0;
  align-items: center;
}
</style>